import { Box, Input, Text } from '@betty/moonlight';
import {
  ElementRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RegistrationFieldProps } from '../../../RegistrationField.types';
import { BottomSheet, SelectableOptionItem } from '@betty/navigation';
import { PROVINCES } from './ManualAddressField.constants';
import { AddressErrors } from './ManualAddressField.types';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  selectRegistrationFormValidationErrors,
  selectRegistrationStepIndex,
  setStepIndex,
} from '../../../../../state';
import { Platform, Pressable } from 'react-native';

function ManualAddressField({
  value,
  error,
  onValue,
}: RegistrationFieldProps<'address', AddressErrors>) {
  const dispatch = useDispatch();
  const unitRef = useRef<ElementRef<typeof Input>>(null);
  const buildingNumberRef = useRef<ElementRef<typeof Input>>(null);
  const streetRef = useRef<ElementRef<typeof Input>>(null);
  const cityRef = useRef<ElementRef<typeof Input>>(null);
  const postalCodeRef = useRef<ElementRef<typeof Input>>(null);
  const [showError, setShowError] = useState(false);
  const errors = useSelector(selectRegistrationFormValidationErrors);
  const addressErrors = errors?.address as AddressErrors;
  const hasErrors = useMemo(() => {
    return Boolean(errors?.address);
  }, [errors?.address]);

  const stepIndex = useSelector(selectRegistrationStepIndex);
  const onProvincePressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'InputSelect',
      detached: Platform.select({
        ios: false,
        web: true,
        android: false,
      }),
      data: {
        selectableOptions: PROVINCES,
        headerNS: 'registration',
        headerKey: 'provincesLabel',
        buttonNS: 'registration',
        buttonKey: 'saveButton',
        centerContent: true,
        allowEmptyField: Platform.select({
          ios: true,
          web: false,
          android: true,
        }),
        maxHeight: '25%',
        defaultValue: value?.province,
        onSelect: (selectedValue: SelectableOptionItem) => {
          onValue({
            ...value,
            province: selectedValue?.text,
            provinceCode: selectedValue?.dynamicValues?.code || '',
          });
          unitRef.current?.focus();
        },
      },
    });
  }, [value, onValue]);

  const handleSubmit = useCallback(() => {
    if (!hasErrors) {
      dispatch(setStepIndex(stepIndex + 1));
    } else {
      setShowError(true);
    }
  }, [hasErrors, dispatch, stepIndex]);

  const handleInputChange = useCallback(
    (fieldName: string, newValue: string) => {
      setShowError(false);
      onValue({ ...value, [fieldName]: newValue });
    },
    [value, onValue],
  );

  useEffect(() => {
    if (
      error?.unit ||
      error?.buildingNumber ||
      error?.streetName ||
      error?.city ||
      error?.postalCode
    ) {
      setShowError(true);
    }
  }, [
    error?.unit,
    error?.buildingNumber,
    error?.streetName,
    error?.city,
    error?.postalCode,
  ]);

  return (
    <Box gap="space-400" marginBottom="space-400">
      <Box>
        <Input
          placeholderNS="registration"
          placeholderKey="countryLabel"
          value={value?.country}
          editable={false}
        />
        <Text
          marginTop="space-100"
          variant="body-s"
          locNS="registration"
          locKey="canadaOnly"
          color="darkSurfaceSecondary"
        ></Text>
      </Box>
      <Pressable onPress={onProvincePressIn}>
        <Input
          placeholderNS="registration"
          placeholderKey="provincesLabel"
          value={value?.province}
          editable={false}
          onPressIn={onProvincePressIn}
        />
      </Pressable>
      <Input
        ref={unitRef}
        placeholderNS="registration"
        placeholderKey="unitLabel"
        value={value?.unit}
        error={showError ? addressErrors?.unit : ''}
        onSubmitEditing={() => buildingNumberRef.current?.focus()}
        onChangeText={(unit) => handleInputChange('unit', unit)}
        enterKeyHint="next"
      />
      {showError && addressErrors?.unit && (
        <Text color="darkSurfaceError" variant="body-s" marginTop="-space-200">
          {addressErrors?.unit}
        </Text>
      )}
      <Input
        ref={buildingNumberRef}
        placeholderNS="registration"
        placeholderKey="buildingNumberLabel"
        autoComplete="off"
        enterKeyHint="next"
        value={value?.buildingNumber}
        error={showError ? addressErrors?.buildingNumber : ''}
        onSubmitEditing={() => streetRef.current?.focus()}
        onChangeText={(buildingNumber) =>
          handleInputChange('buildingNumber', buildingNumber)
        }
      />
      {showError && addressErrors?.buildingNumber && (
        <Text color="darkSurfaceError" variant="body-s" marginTop="-space-200">
          {addressErrors?.buildingNumber}
        </Text>
      )}
      <Input
        ref={streetRef}
        placeholderNS="registration"
        placeholderKey="streetNameLabel"
        enterKeyHint="next"
        value={value?.streetName}
        error={showError ? addressErrors?.streetName : ''}
        onSubmitEditing={() => cityRef.current?.focus()}
        autoComplete="street-address"
        onChangeText={(streetName) =>
          handleInputChange('streetName', streetName)
        }
      />
      {showError && addressErrors?.streetName && (
        <Text color="darkSurfaceError" variant="body-s" marginTop="-space-200">
          {addressErrors?.streetName}
        </Text>
      )}
      <Input
        ref={cityRef}
        placeholderNS="registration"
        placeholderKey="cityLabel"
        enterKeyHint="next"
        value={value?.city}
        error={showError ? addressErrors?.city : ''}
        onSubmitEditing={() => postalCodeRef.current?.focus()}
        autoComplete="address-line2"
        onChangeText={(city) => handleInputChange('city', city)}
      />
      {showError && addressErrors?.city && (
        <Text color="darkSurfaceError" variant="body-s" marginTop="-space-200">
          {addressErrors?.city}
        </Text>
      )}
      <Input
        ref={postalCodeRef}
        placeholderNS="registration"
        placeholderKey="postalCodeLabel"
        enterKeyHint="go"
        value={value?.postalCode}
        error={showError ? addressErrors?.postalCode : ''}
        onSubmitEditing={handleSubmit}
        autoComplete="postal-code"
        onChangeText={(postalCode) =>
          handleInputChange('postalCode', postalCode)
        }
      />
      {showError && addressErrors?.postalCode && (
        <Text color="darkSurfaceError" variant="body-s" marginTop="-space-200">
          {addressErrors?.postalCode}
        </Text>
      )}
    </Box>
  );
}

export default memo(ManualAddressField);
